import { connect, FormikValues } from 'formik'
import { flatten } from 'lodash'
import { v4 } from 'uuid'
import { string } from 'yup'

import Label from '@/components/Label'
import RadioGroup from '@/components/TailwindUI/RadioGroup'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import AutoCompleteVariableInput from '@/pages/SpaceAppBuilder/components/VariableInputDropdown/AutoCompleteVariableInput'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  DocumentSearchStorageStepData,
  FieldTypes,
  StepData,
  Variable
} from '@/pages/SpaceAppBuilder/utilities/interfaces'

type OutputProps = {
  readOnly?: boolean
}

const getValidationSchema = () => {
  return {
    mode: string(),
    document: string()
      .trim()
      .when('mode', {
        is: 'insert',
        then: string().required('Document is required')
      }),
    queryableFeatureId: string()
      .trim()
      .when('mode', {
        is: 'insert',
        then: string().required('Queryable feature is required')
      }),
    insertDocumentId: string()
      .trim()
      .when('mode', {
        is: 'insert',
        then: string().required('Document ID is required')
      }),
    deleteDocumentId: string()
      .trim()
      .when('mode', {
        is: 'delete',
        then: string().required('Document ID is required')
      })
  }
}

const getInitialValues = (stepData: DocumentSearchStorageStepData): StepData => ({
  mode: stepData.mode || 'insert',
  document: stepData.document || '',
  queryableFeatureId: stepData.queryableFeatureId || '',
  insertDocumentId: stepData.insertDocumentId || v4(),
  deleteDocumentId: stepData.deleteDocumentId || ''
})

const searchModes = ['insert', 'delete']

const DocumentSearchStorageOperation = ({ formik, readOnly = false }: OutputProps & FormikValues) => {
  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()

  const featureVariables = flatten(variables).filter(variable => variable?.name?.startsWith('$.feature.'))

  // Only allow variables which are the result of a file upload step
  const fileUploadVariables = variables
    .filter(stepVariables => {
      const uploadedFileProperties = ['name', 'size', 'assetId', 'url', 'text']
      return uploadedFileProperties.every(property =>
        stepVariables.some(v => v.name?.endsWith(property) || v.type === 'any')
      )
    })
    .map(stepVariables => [stepVariables[0]]) // Only return the root variable which includes all properties

  const { setFieldValue, values } = formik
  const mode = values.stepData.mode

  const onSelectVariable = (variable: Variable, formikName: string) => {
    formik.setFieldValue(formikName, variable.name)
  }

  return (
    <div data-testid="document-search-storage-property-panel">
      <RadioGroup
        name="mode"
        value={mode}
        options={searchModes}
        onChange={value => setFieldValue('stepData.mode', value)}
        disabled={readOnly}
        className="mt-2 px-1"
      />
      <div className="mt-4">
        {mode === 'insert' ? (
          <>
            <p className="mt-2 mb-4 text-sm text-gray-500">Insert a document as embeddings in search storage</p>
            <Label required>Document ID</Label>
            <p className="mt-1 mb-2 text-sm text-gray-500">
              This ID ensures uniqueness in search storage. When a document is inserted, any existing document with the
              same ID will be deleted.
            </p>
            <VariableInput
              readOnly={readOnly}
              variables={variables}
              name="stepData.insertDocumentId"
              placeholder="Enter the document ID"
              validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
            />
            <Label required>Document</Label>
            <p className="mt-1 mb-2 text-sm text-gray-500">
              Must be the output from an uploaded file, either from an Upload File operation or from a configuration
              variable.
            </p>
            <VariableInput
              readOnly={readOnly}
              variables={fileUploadVariables}
              name="stepData.document"
              multiline={true}
              placeholder="Enter the uploaded document"
            />
            <Label className="mt-1 mb-2" required>
              Queryable Feature
            </Label>
            <AutoCompleteVariableInput
              readOnly={readOnly}
              name={'stepData.queryableFeatureId'}
              variables={[featureVariables]}
              placeholder="Select queryable feature"
              validVariableTypes={[FieldTypes.STRING]}
              // @ts-expect-error
              onSelectOption={onSelectVariable}
              showVariableButton={false}
            />
          </>
        ) : (
          <>
            <p className="mt-2 mb-4 text-sm text-gray-500">Remove a document from search storage by ID</p>
            <Label required>Document ID</Label>
            <VariableInput
              readOnly={readOnly}
              variables={variables}
              name="stepData.deleteDocumentId"
              placeholder="Enter the document ID"
              validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default connect(DocumentSearchStorageOperation)

export { getInitialValues, getValidationSchema }
